import { useCallback, useImperativeHandle, useMemo } from 'react';
import { Table } from '../table';
import { SelectAllHeaderCheckbox } from './select-all-header-checkbox';
import { useServerSideTable } from './use-server-side-table';
import { useServerSideInfiniteModel } from './use-server-side-infinite-model';

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const EMPTY_OBJ = {};

/**
 * @typedef {Object} ServerSideTableHandle
 * @property {() => void} purgeCache - Clear AG Grid infinite cache and trigger re-fetch
 * @property {() => void} clearSelection - Deselect all rows
 * @property {() => import('ag-grid-community').GridApi | undefined} getApi - Raw AG Grid API
 */

/**
 * @typedef {Object} ServerSideTableOwnProps
 * @property {import('react').Ref<ServerSideTableHandle>} [ref]
 * @property {(params: Record<string, any>) => Promise<{ data?: { items: any[], total: number, [key: string]: any }, error?: string }>} fetchData - Async data fetcher. Receives cleaned params, must return `{ data: { items, total, ...metadata } }` or `{ error }`
 * @property {(params: Record<string, any>) => Record<string, any>} [cleanParams] - Remove empty params before fetching (default: omit nulls, empty strings, empty arrays)
 * @property {(items: any[], total: number, metadata: Record<string, any>) => void} [onDataLoad] - Called after each successful page fetch
 * @property {(error: string) => void} [onDataError] - Called on fetch error
 * @property {Record<string, any>} params - Combined params object from useServerSideSearchParams
 * @property {Record<string, any>} filters - Filter portion of params
 * @property {string} sortBy - Current sort column key
 * @property {'asc' | 'desc' | ''} sortOrder - Current sort direction
 * @property {(updates: Record<string, any>, resetPage?: boolean) => void} updateParams - URL state updater from useServerSideSearchParams
 * @property {string[]} [excludeFilters] - Filter keys to exclude from AG Grid column sync (e.g. ['searchText'])
 * @property {(selectedRows: any[]) => void} [onSelectionChange] - Called when row selection changes
 * @property {number[]} [pageSizeOptions] - Page size selector options (default: [10, 25, 50, 100])
 * @property {boolean} [isStale] - Whether data is stale (dims the table with reduced opacity)
 */

/**
 * Server-side paginated table component.
 * Encapsulates AG Grid's infinite row model, datasource management,
 * cache purging, and selection with a custom header checkbox.
 *
 * Wraps the design system Table component.
 *
 * @param {ServerSideTableOwnProps & import('ag-grid-react').AgGridReactProps & import('../table').TableContainerProps} props
 */
export function ServerSideTable({
  ref,

  // Data fetching
  fetchData,
  cleanParams,
  onDataLoad,
  onDataError,

  // State from useServerSideSearchParams
  params,
  filters,
  sortBy,
  sortOrder,
  updateParams,
  excludeFilters = [],

  // Columns
  columnDefs,
  getRowId,

  // Selection
  onSelectionChange,

  // Config
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  isStale = false,

  // Table passthrough props
  containerProps = {},
  defaultColDef = EMPTY_OBJ,

  // Rest passed to Table
  ...restProps
}) {
  // 1. AG Grid event syncing (existing hook, unchanged)
  const {
    tableRef,
    handleGridReady: baseHandleGridReady,
    handleFilterChanged,
    handleSortChanged,
    handlePaginationChanged,
  } = useServerSideTable({
    filters,
    sortBy,
    sortOrder,
    page: params.page,
    pageSize: params.pageSize,
    excludeFilters,
    onFiltersChange: newFilters => updateParams(newFilters, true),
    onSortChange: ({ sortBy: newSortBy, sortOrder: newSortOrder }) => {
      updateParams({ sortBy: newSortBy, sortOrder: newSortOrder }, true);
    },
    onPaginationChange: ({ page: newPage, pageSize: newPageSize }) => {
      const updates = {};
      if (newPage !== params.page) updates.page = newPage;
      if (newPageSize !== params.pageSize) {
        updates.pageSize = newPageSize;
        updates.page = 1;
      }
      if (Object.keys(updates).length > 0) {
        updateParams(updates, false);
      }
    },
  });

  // Infinite model (datasource, cache purging)
  const { datasource, purgeCache } = useServerSideInfiniteModel({
    fetchData,
    params,
    cleanParams,
    onDataLoad,
    onDataError,
    tableRef,
  });

  // Compose handleGridReady to set datasource + sync state
  const handleGridReady = useCallback(
    event => {
      event.api.setGridOption('datasource', datasource);
      baseHandleGridReady(event);
    },
    [baseHandleGridReady, datasource],
  );

  // Selection handler - calls onSelectionChange with selected rows whenever selection changes
  const handleSelectionChanged = useCallback(
    event => {
      if (!onSelectionChange) return;
      const selectedRows = event.api.getSelectedRows();
      onSelectionChange(selectedRows);
    },
    [onSelectionChange],
  );

  // Imperative handle - expose purgeCache, clearSelection, and getApi to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      purgeCache,
      clearSelection: () => {
        tableRef.current?.getApi?.()?.deselectAll();
      },
      getApi: () => tableRef.current?.getApi?.(),
    }),
    [purgeCache, tableRef],
  );

  const mergedDefaultColDef = useMemo(
    () => ({ unSortIcon: true, ...defaultColDef }),
    [defaultColDef],
  );

  // Stale/loading opacity
  const mergedContainerProps = {
    ...containerProps,
    style: {
      ...containerProps?.style,
      opacity: isStale ? 0.7 : 1,
      transition: 'opacity 0.2s',
    },
  };

  return (
    <Table
      ref={tableRef}
      containerProps={mergedContainerProps}
      columnDefs={columnDefs}
      rowModelType="infinite"
      cacheBlockSize={params.pageSize}
      getRowId={getRowId}
      pagination={true}
      paginationPageSize={params.pageSize}
      paginationPageSizeSelector={pageSizeOptions}
      onPaginationChanged={handlePaginationChanged}
      rowSelection={{
        mode: 'multiRow',
        checkboxes: true,
        headerCheckbox: false,
      }}
      selectionColumnDef={{
        headerComponent: SelectAllHeaderCheckbox,
        suppressHeaderKeyboardEvent: p => {
          const key = p.event.key;
          return key === ' ' || key === 'Enter';
        },
      }}
      defaultColDef={mergedDefaultColDef}
      onGridReady={handleGridReady}
      onSelectionChanged={handleSelectionChanged}
      onSortChanged={handleSortChanged}
      onFilterChanged={handleFilterChanged}
      {...restProps}
    />
  );
}
